import { useCallback, useState } from "react";
import {
  getConversations,
  getConversation,
  deleteConversation,
  sendMessageStream,
} from "../services/aiAssistantService";

const toolLabels = {
  getRestoInfo: "Consultando información de la tienda...",
  getRestoStats: "Consultando estadísticas...",
  getHostsList: "Cargando lista de usuarios...",
  getHostRanking: "Calculando ranking de usuarios...",
  getRecentEvaluations: "Obteniendo evaluaciones recientes...",
  getRecentActivity: "Cargando actividad reciente...",
  getPlatformHelp: "Buscando información de ayuda...",
};

function mapHistoryToMessages(history = []) {
  return history
    .filter((entry) => entry.role === "user" || entry.role === "model")
    .map((entry, index) => ({
      id: `${entry.role}-${index}-${Date.now()}`,
      role: entry.role === "user" ? "user" : "assistant",
      text: entry.parts?.[0]?.text || "",
    }));
}

export const useAIAssistant = () => {
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState(null);

  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [currentConversationTitle, setCurrentConversationTitle] = useState("");
  const [messages, setMessages] = useState([]);

  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState(null);

  const getConversationsList = useCallback(async (limit = 20) => {
    try {
      setConversationsLoading(true);
      setConversationsError(null);
      const res = await getConversations(limit);
      const list = res?.data || res || [];
      setConversations(Array.isArray(list) ? list : []);
      return list;
    } catch (error) {
      console.error("Error obteniendo conversaciones:", error);
      setConversationsError(error.message || "Error al cargar conversaciones");
      setConversations([]);
      return [];
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  const getConversationById = useCallback(async (id) => {
    try {
      setSendLoading(true);
      setSendError(null);
      const res = await getConversation(id);
      const data = res?.data || res;
      const history = data?.history || [];
      const mapped = mapHistoryToMessages(history);
      setMessages(mapped);
      setCurrentConversationId(data?._id || id);
      setCurrentConversationTitle(data?.title || "");
      return data;
    } catch (error) {
      console.error("Error obteniendo conversación:", error);
      setSendError(error.message || "Error al cargar la conversación");
      setMessages([]);
      return null;
    } finally {
      setSendLoading(false);
    }
  }, []);

  const deleteConversationById = useCallback(
    async (id) => {
      try {
        await deleteConversation(id);
        setConversations((prev) => prev.filter((c) => c._id !== id));
        if (currentConversationId === id) {
          setCurrentConversationId(null);
          setCurrentConversationTitle("");
          setMessages([]);
        }
        return true;
      } catch (error) {
        console.error("Error eliminando conversación:", error);
        return false;
      }
    },
    [currentConversationId]
  );

  const startNewChat = useCallback(() => {
    setCurrentConversationId(null);
    setCurrentConversationTitle("");
    setMessages([]);
    setSendError(null);
    setActiveTool(null);
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      if (!text || text.trim().length === 0) return;

      setSendLoading(true);
      setSendError(null);
      setActiveTool(null);
      setIsStreaming(true);

      const userMsg = {
        id: `user-${Date.now()}`,
        role: "user",
        text: text.trim(),
      };
      setMessages((prev) => [...prev, userMsg]);

      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg = {
        id: assistantId,
        role: "assistant",
        text: "",
        isStreaming: true,
      };
      setMessages((prev) => [...prev, assistantMsg]);

      try {
        await sendMessageStream(text.trim(), currentConversationId, {
          onTool: (toolName) => {
            setActiveTool(toolLabels[toolName] || `Consultando ${toolName}...`);
          },
          onChunk: (chunkText) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, text: m.text + (chunkText || "") }
                  : m
              )
            );
          },
          onDone: ({ conversationId, title }) => {
            setCurrentConversationId(conversationId);
            setCurrentConversationTitle(title || "");
            setIsStreaming(false);
            setActiveTool(null);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, isStreaming: false } : m
              )
            );
            // Refrescar lista si es una conversación nueva
            if (!currentConversationId && conversationId) {
              getConversationsList();
            }
          },
          onError: (errMsg) => {
            setSendError(errMsg);
            setIsStreaming(false);
            setActiveTool(null);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, text: `Error: ${errMsg}`, isStreaming: false }
                  : m
              )
            );
          },
        });
      } catch (error) {
        const errMsg = error.message || "Error al enviar el mensaje";
        setSendError(errMsg);
        setIsStreaming(false);
        setActiveTool(null);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, text: `Error: ${errMsg}`, isStreaming: false }
              : m
          )
        );
      } finally {
        setSendLoading(false);
      }
    },
    [currentConversationId, getConversationsList]
  );

  return {
    conversations,
    conversationsLoading,
    conversationsError,
    currentConversationId,
    currentConversationTitle,
    messages,
    sendLoading,
    sendError,
    isStreaming,
    activeTool,
    getConversationsList,
    getConversationById,
    deleteConversationById,
    startNewChat,
    sendMessage,
  };
};
